/** Notification endpoints: the list, read state and preferences. */

import apiBase from './api-base';

/**
 * Notification Types
 */

export type NotificationType = 
  | 'overdue'
  | 'return_due'
  | 'hold_available'
  | 'fine_incurred'
  | 'fine_paid'
  | 'book_reserved'
  | 'system'
  | 'maintenance'
  | 'other';

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';

export interface Notification {
  id: number;
  notification_type: NotificationType;
  title: string;
  message: string;
  severity: NotificationSeverity;
  is_read: boolean;
  action_required: boolean;
  action_url: string | null;
  related_book_id: number | null;
  related_loan_id: number | null;
  created_at: string;
  read_at: string | null;
}

export interface NotificationPreference {
  enable_overdue_notifications: boolean;
  enable_return_due_notifications: boolean;
  enable_hold_notifications: boolean;
  enable_fine_notifications: boolean;
  enable_system_notifications: boolean;
  email_notifications: boolean;
  in_app_notifications: boolean;
  return_due_days_advance: number;
}

export interface NotificationStats {
  total_notifications: number;
  unread_count: number;
  by_type: Record<NotificationType, number>;
  by_severity: Record<NotificationSeverity, number>;
  action_required_count: number;
  recent_notifications: Notification[];
}

export interface NotificationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  unread_count: number;
  results: Notification[];
}

export interface NotificationQueryParams {
  unread_only?: boolean;
  type?: NotificationType;
  severity?: NotificationSeverity;
  limit?: number;
}

/**
 * Notifications API Client
 */
export const notificationsApi = {
  /**
   * Get all notifications for the current user
   */
  getNotifications: async (params?: NotificationQueryParams): Promise<NotificationListResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params?.unread_only !== undefined) {
      queryParams.append('unread_only', params.unread_only.toString());
    }
    if (params?.type) {
      queryParams.append('type', params.type);
    }
    if (params?.severity) {
      queryParams.append('severity', params.severity);
    }
    if (params?.limit) {
      // Standard pagination: ``limit`` maps onto ``page_size`` (max 100).
      queryParams.append('page_size', Math.min(params.limit, 100).toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/notifications/${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiBase.get<NotificationListResponse>(url);
    return response.data;
  },

  /**
   * Get unread notifications only
   */
  getUnreadNotifications: async (limit?: number): Promise<NotificationListResponse> => {
    return notificationsApi.getNotifications({
      unread_only: true,
      limit: limit || 50
    });
  },

  /**
   * Get notifications by type
   */
  getNotificationsByType: async (type: NotificationType, limit?: number): Promise<NotificationListResponse> => {
    return notificationsApi.getNotifications({
      type,
      limit: limit || 50
    });
  },

  /**
   * Get overdue notifications
   */
  getOverdueNotifications: async (): Promise<NotificationListResponse> => {
    return notificationsApi.getNotificationsByType('overdue');
  },

  /**
   * Mark a specific notification as read
   */
  markAsRead: async (notificationId: number): Promise<Notification> => {
    const response = await apiBase.post<Notification>(
      `/notifications/${notificationId}/mark-read/`,
      {}
    );
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ updated_count: number }> => {
    const response = await apiBase.post<{ updated_count: number }>(
      '/notifications/mark-all-read/',
      {}
    );
    return response.data;
  },

  /**
   * Delete a specific notification
   */
  deleteNotification: async (notificationId: number): Promise<void> => {
    await apiBase.delete(`/notifications/${notificationId}/`);
  },

  /**
   * Get notification statistics
   */
  getNotificationStats: async (): Promise<NotificationStats> => {
    const response = await apiBase.get<NotificationStats>('/notifications/stats/');
    return response.data;
  },

  /**
   * Get user notification preferences
   */
  getPreferences: async (): Promise<NotificationPreference> => {
    const response = await apiBase.get<NotificationPreference>('/notifications/preferences/');
    return response.data;
  },

  /**
   * Update user notification preferences
   */
  updatePreferences: async (preferences: Partial<NotificationPreference>): Promise<NotificationPreference> => {
    const response = await apiBase.put<NotificationPreference>(
      '/notifications/preferences/',
      preferences
    );
    return response.data;
  },
};

export default notificationsApi;
